import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * Back-in-stock "notify me" (blocks/ssc-notify-me.liquid).
 * Fabric doesn't re-render custom blocks on variant change, so this element listens for the product
 * select event on its section and uses the resolved variant: sold out shows the block (CSS hides the
 * buy buttons), available hides it again. A different variant resets the form and collapses it.
 * Submitting is handled by the shared delegated handler in assets/ssc-klaviyo-signup.js.
 *
 * @typedef {{ toggle: HTMLButtonElement, panel: HTMLElement, signup: HTMLElement, email: HTMLInputElement }} Refs
 * @extends {Component<Refs>}
 */
class SscNotifyMe extends Component {
  requiredRefs = ['toggle', 'panel', 'signup'];

  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    this.closest('.shopify-section, dialog')?.addEventListener(StandardEvents.productSelect, this.handleProductSelect, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#abortController = undefined;
  }

  /** Reveals the email form and moves focus into it. */
  toggle() {
    const { toggle, panel, email } = this.refs;
    const open = toggle.getAttribute('aria-expanded') !== 'true';
    toggle.setAttribute('aria-expanded', String(open));
    panel.hidden = !open;
    if (open) email?.focus();
  }

  /** Collapses the form and clears its state. */
  #reset() {
    const { toggle, panel, signup, email } = this.refs;
    toggle.setAttribute('aria-expanded', 'false');
    panel.hidden = true;
    if (email) email.value = '';
    // @ts-ignore - set by assets/ssc-klaviyo-signup.js
    window.SSCKlaviyo?.reset?.(signup);
  }

  /**
   * @param {ProductSelectEvent} event - The product select event.
   */
  handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail || !this.isConnected) return;

        const { newProduct, productId, resource } = detail;
        if (newProduct) {
          this.dataset.productId = String(newProduct.id);
        } else if (productId && String(productId) !== this.dataset.productId) {
          return;
        }

        const variantId = resource?.id ? String(resource.id) : '';
        const soldOut = Boolean(resource) && resource.available === false;
        const { signup, toggle } = this.refs;
        const hadFocus = this.contains(document.activeElement);

        if (signup.dataset.variantId !== variantId) {
          signup.dataset.variantId = variantId;
          this.#reset();
        }
        this.dataset.soldOut = String(soldOut);

        // Keep keyboard users on the replacement control when the buttons swap
        if (soldOut && hadFocus && toggle.getAttribute('aria-expanded') !== 'true') toggle.focus();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[ssc-notify-me] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('ssc-notify-me')) {
  customElements.define('ssc-notify-me', SscNotifyMe);
}
